import axios from "./axiosInstance";
import config from "./config";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

// Type Definitions
export interface SupportTicketAttachment {
  filename: string;
  contentType: string;
  base64: string;
}

export interface SupportTicketRequest {
  name: string;
  email: string;
  subject: string;
  category: string;
  priority: string;
  description: string;
  attachments?: SupportTicketAttachment[];
  // Hidden fields
  role: string;
  tenantId: string;
  tenantName: string;
  userId: string;
  authSource: string;
}

export interface SupportTicketResponse {
  success: boolean;
  message: string;
  data?: {
    ticketId: string;
    status: string;
    createdAt: string;
  };
  error?: string;
}

/**
 * Convert a File object to base64 string
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Convert an array of File objects to SupportTicketAttachment array
 */
export const filesToAttachments = async (
  files: File[]
): Promise<SupportTicketAttachment[]> => {
  const attachments: SupportTicketAttachment[] = [];

  for (const file of files) {
    try {
      const base64 = await fileToBase64(file);
      attachments.push({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        base64,
      });
    } catch (error) {
      captureException(error);
    }
  }

  return attachments;
};

/**
 * Submit a new support ticket
 *
 * @param token - Authentication token
 * @param ticketData - Support ticket data including attachments
 * @returns Promise with ticket creation response
 */
export const submitSupportTicket = async (
  token: string | null,
  ticketData: SupportTicketRequest
): Promise<SupportTicketResponse> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/support-tickets`,
      ticketData,
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    throw error.response?.data || { success: false, message: 'Failed to submit support ticket' };
  }
};

/**
 * Get current user's support tickets
 *
 * @param token - Authentication token
 * @returns Promise with array of tickets
 */
export const getUserSupportTickets = async (token: string | null) => {
  try {
    const response = await axios.get(`${BASE_URL}/support-tickets`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    throw error.response?.data || { success: false, message: 'Failed to fetch support tickets' };
  }
};

// Response types
export interface TicketResponse {
  id: string;
  ticket_id: string;
  responder_id: string;
  responder_name: string;
  responder_email?: string;
  response_text: string;
  attachments?: string[];
  is_internal: boolean;
  is_from_submitter: boolean;
  created_at: string;
  updated_at: string;
}

export interface AddResponseRequest {
  message: string;
  attachments?: SupportTicketAttachment[];
}

/**
 * Get responses for a specific support ticket
 *
 * @param token - Authentication token
 * @param ticketId - The ticket ID
 * @returns Promise with array of responses
 */
export const getTicketResponses = async (
  token: string | null,
  ticketId: string
): Promise<{ success: boolean; data: TicketResponse[] }> => {
  try {
    const response = await axios.get(
      `${BASE_URL}/support-tickets/${ticketId}/responses`,
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    throw error.response?.data || { success: false, message: 'Failed to fetch responses' };
  }
};

/**
 * Add a response to a support ticket
 *
 * @param token - Authentication token
 * @param ticketId - The ticket ID
 * @param data - The response data including message and optional attachments
 * @returns Promise with the created response
 */
export const addTicketResponse = async (
  token: string | null,
  ticketId: string,
  data: AddResponseRequest
): Promise<{ success: boolean; data: TicketResponse }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/support-tickets/${ticketId}/responses`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error: any) {
    throw error.response?.data || { success: false, message: 'Failed to add response' };
  }
};
